import { useCallback } from "react";
import { Release } from "../types";
import { doesUserHaveAccess } from "../utils/doesUserHaveAccess";
import { doesReleaseHaveGoldDiscount } from "../utils/doesReleaseHaveGoldDiscount";
import {
  FunnelConfigTypes,
  FunnelOnSuccessResult,
  FunnelOptionsTypes,
} from "../components/funnel/types";
import { FUNNEL_TYPES } from "../components/funnel/utils/contants";
import { useUserData } from "./useUserData";

interface UseReleasePurchaseFlowProps {
  setFunnelConfig: (config?: FunnelConfigTypes) => void;
  options?: FunnelOptionsTypes;
}

/**
 * Shared hook for handling release purchase flow logic
 * Used by both useReleaseInfoPaywall and useBuyPreviewContext
 */
export function useReleasePurchaseFlow({
  setFunnelConfig,
  options,
}: UseReleasePurchaseFlowProps) {
  const { userData } = useUserData();

  const handleSuccessFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      const { data, lastFunnelType } = result;
      const purchaseRelease = data?.[1]?.data?.payload?.results;
      const { userHasBuyAccess, userHasGoldAccess } = doesUserHaveAccess(data);
      const funnelWasAuth =
        lastFunnelType &&
        [
          FUNNEL_TYPES.signup,
          FUNNEL_TYPES.signupEmailPassword,
          FUNNEL_TYPES.login,
        ].includes(lastFunnelType);

      let type;
      if (userHasBuyAccess) {
        if (!funnelWasAuth) {
          setFunnelConfig?.();
          return;
        }
        type = FUNNEL_TYPES.purchaseRelease;
      } else if (userHasGoldAccess) {
        type = funnelWasAuth ? FUNNEL_TYPES.goldAccess : FUNNEL_TYPES.congrats;
      }

      setFunnelConfig?.({
        type,
        context: {
          ...result.context,
          purchaseRelease,
        },
        options: {
          ...result.options,
          onSuccess: () => {
            setFunnelConfig?.();
          },
        },
      });
    },
    [setFunnelConfig],
  );

  const handlePostCongratsFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      setFunnelConfig?.({
        type: FUNNEL_TYPES.purchaseRelease,
        context: result.context,
        options: {
          ...result.options,
          onSuccess: handleSuccessFlow,
        },
      });
    },
    [setFunnelConfig, handleSuccessFlow],
  );

  const handlePostPaymentFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      console.log("handlePostPaymentFlow", result);
      const release = result.context?.purchaseRelease;
      if (
        (result.lastFunnelType === FUNNEL_TYPES.subscription ||
          result.lastFunnelType === FUNNEL_TYPES.offerPaywall) &&
        doesReleaseHaveGoldDiscount(release as Release)
      ) {
        handlePostCongratsFlow(result);
        // setFunnelConfig?.({
        //   type: FUNNEL_TYPES.congrats,
        //   context: result.context,
        //   options: {
        //     ...result.options,
        //     onSuccess: handlePostCongratsFlow,
        //   },
        // });
      } else {
        handleSuccessFlow(result);
      }
    },
    [handlePostCongratsFlow, handleSuccessFlow, setFunnelConfig],
  );

  const handlePostAuthFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      const { userHasAccess } = doesUserHaveAccess(result.data);

      if (userHasAccess) {
        handleSuccessFlow(result);
      } else {
        const type = result.nextFunnelType;

        setFunnelConfig?.({
          type,
          context: result.context,
          options: {
            ...result.options,
            onSuccess: handlePostPaymentFlow,
          },
        });
      }
    },
    [handlePostPaymentFlow, handleSuccessFlow, setFunnelConfig],
  );

  if (!userData) {
    return (config: FunnelConfigTypes) => {
      setFunnelConfig({
        type: FUNNEL_TYPES.signup,
        context: config.context,
        options: {
          ...config.options,
          skipOnboarding: true,
          onSuccess: (result: FunnelOnSuccessResult) =>
            handlePostAuthFlow({ ...result, nextFunnelType: config.type }),
        },
      });
    };
  }
  return (config: FunnelConfigTypes) => {
    setFunnelConfig({
      ...config,
      options: {
        ...config.options,
        ...options,
        onSuccess: handlePostPaymentFlow,
      },
    });
  };
}
